import classNames from 'classnames';
import { useFormikContext } from 'formik';
import { Alert, Form, Tab } from 'react-bootstrap';
import Select from 'react-select';
import type { OptionProps } from 'react-select';
import type { FormikErrors } from 'formik';
import type { TrackerSettings } from '../types';

interface MainProps {
  weeklyUpdateDays: number | null;
  updateDomainHistory?: string[];
}

const Main = ({ weeklyUpdateDays, updateDomainHistory }: MainProps) => {
  const { values, setFieldValue, errors } = useFormikContext<TrackerSettings>();
  const numOfSelectedDays = values.updated_on_week.length;
  const isWeeklyDaysExceeded =
    weeklyUpdateDays != null && weeklyUpdateDays < numOfSelectedDays;

  return (
    <Tab.Pane className="my-3" eventKey="updateSettings">
      <h5 className="fw-bold my-3 mt-5 fs-0">تنظیمات به‌روزرسانی</h5>
      <div className="d-flex gap-2 align-items-center">
        <Form.Check
          type="checkbox"
          id="enable_automatic_update"
          name="enable_automatic_update"
          label={
            <div className="fs--1 cursor-pointer">
              فعال کردن به‌روزرسانی خودکار{' '}
            </div>
          }
          checked={values.enable_automatic_update}
          onChange={({ target }) => {
            setFieldValue('enable_automatic_update', target.checked);
          }}
        />
      </div>
      <div className="my-3">
        {isWeeklyDaysExceeded && (
          <Alert variant={'danger'} className={'fs--1 p-2'}>
            در طرح شما فقط {weeklyUpdateDays} روز قابل انتخاب هست. تعداد
            روزهای انتخاب‌شده مجاز نیست؛ لطفا آن را به {weeklyUpdateDays} روز
            تغییر دهید.
          </Alert>
        )}
        <div className="fs--1 my-2">
          به‌روزرسانی خودکار در چه روزهایی از هفته صورت گیرد؟
        </div>
        <TimeSelector
          disabled={!values.enable_automatic_update}
          timesSelect={values.updated_on_week}
          weeklyUpdateDays={weeklyUpdateDays}
          setTimesSelect={val => setFieldValue('updated_on_week', val)}
          errors={errors}
        />
        <div className="fs--2 m-2 my-1 text-justify">
          * به‌روزرسانی هر روز کلمات صرفاً حجم داده‌ها رو افزایش میده و تحلیل رو
          برای شما دشوارتر می‌کنه (به طور متوسط تحلیل ۳۰۰ کلمه ۶ ساعت زمان
          می‌بره). این امر ممکنه باعث شه برخی فرصت‌ها و مشکلات رو مشاهده نکنید.
          پیشنهاد می‌کنیم به جای به‌روزرسانی هر روز، به‌روزرسانی در روزهای فرد
          یا زوج رو انتخاب کنید و تحلیل‌های خودتون رو در این روزها انجام
          بدید.{' '}
        </div>
        {updateDomainHistory && (
          <Alert
            variant={'info'}
            className={'fs--1 p-2 mt-3'}
          >
            <span> به‌روزرسانی های هفته جاری : {updateDomainHistory.join('، ')} </span><br />
          </Alert>)}

        <Alert
          variant={'info'}
          className={'fs--1 p-2'}
        >
          <span> براساس طرح خریداری شده برای این دامنه امکان بروزرسانی <b> {weeklyUpdateDays} روز </b>  در هفته وجود دارد.
          </span>
        </Alert>
      </div>
    </Tab.Pane>
  );
};

export default Main;

interface TimeSelectorProps {
  timesSelect: string[];
  setTimesSelect: (val: string[]) => void;
  disabled: boolean;
  errors: FormikErrors<TrackerSettings>;
  weeklyUpdateDays: number | null;
}

const TimeSelector = ({
  timesSelect,
  setTimesSelect,
  disabled,
  errors,
  weeklyUpdateDays
}: TimeSelectorProps) => {
  return (
    <>
      <Select
        classNamePrefix="react-select"
        isDisabled={disabled}
        menuShouldScrollIntoView={true}
        closeMenuOnSelect={false}
        placeholder="انتخاب زمان های به‌روزرسانی"
        styles={{
          menu: (baseStyles, state) => ({
            position: 'absolute',
            // overflow: 'auto',
            zIndex: 9999,
            width: '100%'
          }),
          control: (baseStyles, state) => ({
            ...baseStyles,
            direction: 'rtl !important',
            opacity: disabled ? 0.6 : 1,
            paddingLeft: '10px',
            paddingRight: '10px',
            color: 'var(--falcon-emphasise) !important',
            boxShadow: 'none !important',
            borderRadius: '5px 5px 5px 5px !important',
            width: '100% !important',
            cursor: 'pointer',
            borderColor:
              errors.updated_on_week &&
              'var(--falcon-form-invalid-border-color) !important'
          }),
          input: (baseStyles, state) => ({
            ...baseStyles,
            direction: 'ltr !important',
            color: 'var(--falcon-emphasise) !important',
            boxShadow: 'none !important'
          })
        }}
        isOptionDisabled={() =>
          timesSelect.length >= (weeklyUpdateDays ?? 0)
        }
        components={{ Option: OptionLayout }}
        onChange={e => {
          const v = e.some(x => x.value === '7') ? ['7'] : e.map(x => x.value);
          setTimesSelect(v);
        }}
        isMulti
        value={timesSelect.map(item => {
          return options.find(x => x.value === item);
        })}
        isSearchable={true}
        noOptionsMessage={() => 'یافت نشد'}
        options={options}
      />
      <Form.Control.Feedback
        className="d-block"
        type={errors.updated_on_week ? 'invalid' : 'valid'}
      >
        {errors.updated_on_week}
      </Form.Control.Feedback>
    </>
  );
};

let options = [
  { label: 'شنبه', value: '0' },
  { label: 'یکشنبه', value: '1' },
  { label: 'دوشنبه', value: '2' },
  { label: 'سه‌شنبه', value: '3' },
  { label: 'چهارشنبه', value: '4' },
  { label: 'پنج‌شنبه', value: '5' },
  { label: 'جمعه', value: '6' },
];



const OptionLayout = (props: OptionProps<{ label: string; value: string }>) => {
  const { innerProps, innerRef } = props;
  return (
    <>
      <style>{`
          .customized-searchbox-option:hover{
            background-color:var(--falcon-gray-link-hover-300) !important;
          }
          .customized-searchbox-option{
            background-color:var(--falcon-tertiary-bg) !important;
          }
          `}</style>
      <div
        ref={innerRef}
        {...innerProps}
        style={{
          direction: 'ltr',
          backgroundColor: props.isSelected
            ? 'var(--falcon-gray-link-hover-100)'
            : 'var(--falcon-dropdown-bg-global)'
        }}
        className={classNames(
          'p-2 cursor-pointer border border-1 customized-searchbox-option',
          {
            'disabled-link':
              props.data.label !== '7'
                ? props.getValue().some(item => item.value === '7')
                : false
          }
        )}
      >
        <div className="fs-0 text-900 d-flex justify-content-between">
          {props.data.label}
        </div>
        <div className="text-secondary fs--2"></div>
      </div>
    </>
  );
};
